import React, { useEffect, useState } from 'react';
import SignupEmailForm from '../components/SignupEmailForm';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import WaitingPage from '../components/WaitingPage';
import EmailGate from '../components/EmailGate';
import { Grid, CircularProgress, Box, Typography } from '@mui/material';
import signup_bg from '../../../assets/signup/signup_bg.svg';
import { checkEmailStatus } from '../dataHandlers/checkEmailStatusHandler';
import { SignupReferralProvider, useSignupReferral } from '../hooks/useSignupReferral';

const SignupPageContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signupPath } = useSignupReferral();
  const [loading, setLoading] = useState(false);
  const [resolvedState, setResolvedState] = useState(location.state);
  const [hasCheckedBackend, setHasCheckedBackend] = useState(false);

  useEffect(() => {
    const email = searchParams.get('email') || location.state?.email;
    const hasState = location.state?.stage;

    if (hasState) {
      setResolvedState(location.state);
      return;
    }

    if (!email || hasCheckedBackend || loading) {
      return;
    }

    const checkEmailAndNavigate = async () => {
      setLoading(true);
      setHasCheckedBackend(true);

      try {
        const response = await checkEmailStatus(email);
        const { status, submittedAt } = response;

        const handleStatus = {
          PENDING_ACTIVATION: () => {
            const newState = {
              stage: 'waiting',
              email,
              submittedAt: submittedAt || new Date().toISOString(),
            };
            setResolvedState(newState);
            navigate(signupPath, { state: newState, replace: true });
          },
          ACTIVE: () => {
            navigate(`/login?email=${encodeURIComponent(email)}`, { replace: true });
          },
          NOT_FOUND: () => {
            const newState = {
              stage: 'signup',
              email,
            };
            setResolvedState(newState);
            navigate(signupPath, { state: newState, replace: true });
          },
        };

        const handler = handleStatus[status];
        if (handler) {
          handler();
        }
      } catch (error) {
        console.error('Error checking email status:', error);
        setHasCheckedBackend(false);
      } finally {
        setLoading(false);
      }
    };

    checkEmailAndNavigate();
  }, [searchParams, location.state, signupPath, navigate, hasCheckedBackend, loading]);

  const state = resolvedState || location.state;

  if (loading) {
    return (
      <Grid
        container
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          p: '20px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Checking account status...
          </Typography>
        </Box>
      </Grid>
    );
  }

  const getComponent = () => {
    if (!state || !state.stage) {
      return <EmailGate />;
    }

    if (state.stage === 'waiting') {
      return <WaitingPage />;
    }

    if (state.stage === 'signup') {
      return <SignupEmailForm initialEmail={state.email} />;
    }

    return <EmailGate />;
  };

  return (
    <Grid
      container
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        p: '20px',
      }}
    >
      <Grid
        size={{ xs: 0, lg: 7, xl: 6 }}
        sx={{
          display: { sm: 'none', lg: 'flex' },
        }}
      >
        <img src={signup_bg} alt="signup background" width="100%" style={{ display: 'flex' }} />
      </Grid>
      <Grid size={{ xs: 1, sm: 2, md: 3, lg: 1, xl: 2 }} />
      <Grid size={{ xs: 10, sm: 8, md: 6, lg: 3, xl: 3 }}>{getComponent()}</Grid>
      <Grid size={{ xs: 1, sm: 2, md: 3, lg: 1, xl: 1 }} />
    </Grid>
  );
};

const SignupPage = () => (
  <SignupReferralProvider>
    <SignupPageContent />
  </SignupReferralProvider>
);

export default SignupPage;
